import React, { useState } from "react";
import {
  AppBar,
  Box,
  Typography,
  Container,
  Grid,
  Chip,
  Stack,
  Paper,
  Divider,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import { motion } from "framer-motion";
import { Link } from "react-scroll";

// Assets & Components
import PyaePhyoSweImage from "./PyaePhyoSwe.jpg";
import CodeIcon from "@mui/icons-material/Code";
import StorageIcon from "@mui/icons-material/Storage";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { projects, workExperience, skills, stats } from "./data/data";
import CallBtnComponent from "./components/CallBtnComponent";
import CheckBtnComponent from "./components/CheckBtnComponent";

const MotionBox = motion(Box);

export default function PortfolioPage() {
  function MobileMenu() {
    const [open, setOpen] = useState(false);
    return (
      <>
        <IconButton
          edge="end"
          sx={{ display: { xs: "block", md: "none" } }}
          onClick={() => setOpen(true)}
        >
          <MenuIcon />
        </IconButton>
        <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
          <Box sx={{ width: 200, mt: 2 }}>
            <List>
              {["about", "skills", "experience", "projects", "contact"].map(
                (item) => (
                  <ListItem button key={item}>
                    <Link
                      to={item}
                      smooth
                      duration={500}
                      offset={-70}
                      style={{ cursor: "pointer", width: "100%" }}
                      onClick={() => setOpen(false)}
                    >
                      <ListItemText
                        primary={item.charAt(0).toUpperCase() + item.slice(1)}
                      />
                    </Link>
                  </ListItem>
                )
              )}
            </List>
          </Box>
        </Drawer>
      </>
    );
  }
  return (
    <Box sx={{ bgcolor: "#fdfdfd", fontFamily: '"Poppins", sans-serif' }}>
      {/* Navigation Bar */}
      <AppBar
        position="fixed"
        elevation={2}
        sx={{
          bgcolor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, md: 4 } }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: "#0e0e0e" }}>
            Pyae Phyo Swe
          </Typography>

          {/* Desktop Nav */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2 }}>
            {["about", "skills", "experience", "projects", "contact"].map(
              (item) => (
                <Link
                  key={item}
                  to={item}
                  smooth
                  duration={500}
                  offset={-70}
                  style={{ cursor: "pointer" }}
                >
                  <Button
                    variant="text"
                    sx={{
                      textTransform: "none",
                      color: "#333",
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        color: "#1976d2",
                      },
                    }}
                  >
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </Button>
                </Link>
              )
            )}
          </Box>

          {/* Mobile Drawer */}
          <MobileMenu />
        </Toolbar>
      </AppBar>

      <Box height="64px" />
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 4, md: 2 }} // 👈 reduced gap between image and text
          alignItems="flex-start"
          justifyContent="flex-start"
          id='about'
        >
          {/* Image section (left on desktop, top on mobile) */}
          <Box
            sx={{
              order: { xs: 1, md: 1 },
              width: { xs: "100%", md: "30%" }, // 👈 reduce md width to tighten layout
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <motion.img
              src={PyaePhyoSweImage}
              alt="Pyae Phyo Swe"
              style={{
                width: "100%",
                maxWidth: 280,
                borderRadius: "50%",
              }}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1 }}
            />
          </Box>

          {/* Text section */}
          <Box
            sx={{
              order: { xs: 2, md: 2 },
              width: { xs: "100%", md: "60%" },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <MotionBox
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h3"
                fontWeight={800}
                gutterBottom
                sx={{ fontSize: { xs: "2rem", md: "3rem" } }}
              >
                Hey There,
                <br />
                I'm Pyae Phyo Swe
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Software Engineer | Founder | Mobile & Web Full Stack Developer
              </Typography>
              <Typography
                sx={{
                  mt: 2,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.6,
                }}
              >
                Building efficient software that transforms complexity into
                clarity and
                <br />
                drives real business impact.
              </Typography>
              <Box mt={3}>
                <CallBtnComponent />
              </Box>
            </MotionBox>
          </Box>
        </Stack>

        {/* Stats */}
        <Grid container spacing={3} mt={6}>
          {stats.map((item, i) => (
            <Grid
              item
              xs={i === 2 ? 12 : 6} // 👈 This is the key part
              sm={6}
              md={4}
              key={i}
            >
              <Paper
                elevation={3}
                sx={{ p: 4, textAlign: "center", borderRadius: 3 }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.2 }}
                >
                  {item.icon}
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color="#00CFC8"
                    sx={{ mt: 1 }}
                  >
                    {item.value}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {item.label}
                  </Typography>
                </motion.div>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Skills */}
        <Box mt={8}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            What I Can Help You With
          </Typography>
          <Typography sx={{ maxWidth: 720, mb: 4 }}>
            I collaborate with teams and businesses to craft scalable,
            user-centric solutions that solve real problems and accelerate
            growth.
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                label: "Website Design",
                count: "15 Projects",
                color: "#E0F7FA",
              },
              {
                label: "Mobile App Design",
                count: "5 Projects",
                color: "#FFF3E0",
              },
              {
                label: "Technology Consultant",
                count: "30 Projects",
                color: "#EDE7F6",
              },
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper sx={{ p: 3, bgcolor: item.color, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.count}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Technical Skills */}
        <Box id='skills' mt={6}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            My Technical Skills
          </Typography>
          <Grid container spacing={4}>
            {[
              ["Frontend", CodeIcon, skills.frontend],
              ["Backend", DeveloperModeIcon, skills.backend],
              ["Database / Tools", StorageIcon, skills.tools],
              ["Automation & Analysis", QueryStatsIcon, skills.automation],
            ].map(([label, Icon, skillList], idx) => (
              <Grid item xs={12} md={6} key={label}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  mb={1}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Icon sx={{ mr: 1 }} /> {label}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  rowGap={1} // 👈 add vertical gap on wrap
                  flexWrap="wrap"
                  useFlexGap
                >
                  {skillList.map((skill) => (
                    <Chip
                      key={skill}
                      label={skill}
                      color="primary"
                      variant="outlined"
                      sx={{
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "#005B96",
                          color: "#fff",
                          borderColor: "#005B96",
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Work Experience */}
        <Box id='experience' mt={8}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            My Work Experience
          </Typography>
          <Divider sx={{ mb: 4 }} />
          <Timeline position="right" sx={{ pl: 0 }}>
            {workExperience.map((job, i) => (
              <TimelineItem key={i}>
                <TimelineOppositeContent
                  sx={{
                    flex: "none",
                    minWidth: 120,
                    pr: 2,
                    textAlign: "left",
                    fontSize: "0.85rem",
                    color: "text.secondary",
                  }}
                >
                  {job.period}
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot color="primary" />
                  {i !== workExperience.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Typography fontWeight={600}>
                    {job.role} — {job.company}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {job.desc}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Box>

        {/* Projects */}
        <Box id='projects' mt={8}>
          <Container maxWidth="xl">
            <Typography variant="h5" fontWeight={700} gutterBottom>
              My Latest Works
            </Typography>
            <Grid container columnSpacing={3} rowSpacing={4} mt={3}>
              {projects.map((proj, i) => (
                <Grid item size={{ lg: 4, xs: 12 }} key={i}>
                  <MotionBox
                    component={Paper}
                    sx={{
                      width: "100%",
                      borderRadius: 2,
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      height: "100%",
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.2, duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <img
                      src={proj.img}
                      alt={proj.name}
                      style={{
                        width: 200,
                        height: 150,
                        objectFit: "contain", // 👈 Important to show full image without cropping
                        borderRadius: 12,
                        marginBottom: 16,
                      }}
                    />

                    <Typography variant="subtitle1" fontWeight={600}>
                      {proj.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, textWrap: "wrap", width: 300 }}
                    >
                      {proj.desc}
                    </Typography>
                    <CheckBtnComponent url={proj.link} />
                  </MotionBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Footer */}
        <Box id='contact' mt={10} textAlign="center" py={4}>
          <Typography variant="h6" fontWeight={700}>
            Let’s make something amazing together.
          </Typography>
          <CallBtnComponent />
          {/* Email */}
  <Typography variant="body2" color="text.secondary" mt={2}>
    Or reach me at{" "}
    <a
      href="mailto:dev.pyaephyoswe@gmail.com"
      style={{ color: "#1976d2", textDecoration: "none", fontWeight: 500 }}
    >
      dev.pyaephyoswe@gmail.com
    </a>
  </Typography>
        </Box>
      </Container>
    </Box>
  );
}
